import i18n from '../../../../i18n';
import { ATS } from '../../../../interfaces';
import { queryClient } from '../../../client';
import { AtsHttpClient } from '../../ats-http-client';
import { settings } from '../../settings';
import { Title } from '../../titles/types/title';
import { Product, Purchase, Rental, SubscribedProduct } from './types';
import { purchasesQuery, rentalsQuery } from './queries';
import { createProducts, createPurchase, createRental, sortTitlesByProduct } from './utils';

const client = new AtsHttpClient();

export async function fetchProducts(platformTitleId: string): Promise<Product[]> {
  const products = await client.get<ATS.ProductForTitle[]>(`/products/title/${platformTitleId}`, settings.me());
  return createProducts(products);
}

export async function fetchPurchases(): Promise<Purchase[]> {
  const products = await client.get<ATS.ProductSubscription[]>('/me/purchases', settings.me());
  return products.map(createPurchase);
}

export async function fetchRentals(): Promise<Rental[]> {
  const rentals = await client.get<ATS.ProductSubscription[]>('/me/rentals', settings.me());
  return rentals.map(createRental);
}

/**
 * A list of purchased and rented products combined.
 */
export async function getSubscribedProducts(): Promise<SubscribedProduct[]> {
  const purchases = await queryClient.fetchQuery(purchasesQuery());
  const rentals = await queryClient.fetchQuery(rentalsQuery());
  return [...rentals, ...purchases];
}

function setPreferredVodProvider(title: Title): Title {
  return {
    ...title,
    orderProvidersBy: {
      ...title.orderProvidersBy,
      id: title.orderProvidersBy?.id ?? null,
      type: 'vod',
    },
  };
}

export async function getPurchasedTitles(): Promise<Title[]> {
  const titles = await client.get<Title[]>('/me/purchases/titles', settings.me());
  return titles.map(setPreferredVodProvider);
}

export async function getRentedTitles(): Promise<Title[]> {
  const titles = await client.get<Title[]>('/me/rentals/titles', settings.me());
  return titles.map(setPreferredVodProvider);
}

export async function getSubscribedTitles(): Promise<Title[]> {
  try {
    const purchases = await getPurchasedTitles();
    const rentals = await getRentedTitles();
    const products = await getSubscribedProducts();
    const titles = purchases.concat(rentals);
    return sortTitlesByProduct(titles, products);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function subscribeToProduct(productId: string, platformTitleId: string) {
  productId = productId.split('/')!.pop()!;

  const response = await client.post<ATS.PurchaseResponse>(
    `/products/subscribe/${productId}`,
    { platformTitleId: platformTitleId },
    settings.me(),
  );

  if ('errors' in response && response.errors.length > 0) {
    throw new Error(
      response.errors[0] === "The subscriber's credit not balance."
        ? i18n.t('purchase insufficient funds message')
        : i18n.t('error sorry please try again later'),
    );
  }

  return response;
}
